// src/components/layout/Footer.jsx
import React, { useState, useEffect } from "react";
import { FaInstagram, FaTelegram, FaWhatsapp, FaLinkedin } from "react-icons/fa";
import { HiShieldCheck } from "react-icons/hi";
import { MdLocalShipping, MdSupportAgent } from "react-icons/md";
import { BsAwardFill } from "react-icons/bs";

// ─── Sub-components ────────────────────────────────

const TrustBadge = ({ icon: Icon, title, subtitle, color, isLast }) => (
  <div
    className={`flex flex-col items-center justify-center gap-1.5 px-3 py-6 text-center transition-colors
                ${!isLast ? "border-b md:border-b-0 md:border-l border-[#e8ede4]" : ""}`}
  >
    <div
      className={`w-11 h-11 rounded-full flex items-center justify-center mb-1 ${color.bg}`}
    >
      <Icon className={`text-xl ${color.icon}`} />
    </div>
    <p className="text-xs font-bold text-[#2d2d2d] leading-5">{title}</p>
    <p className="text-[10px] text-gray-400 leading-4">{subtitle}</p>
  </div>
);

// ─── Data ────────────────────────────────

const trustBadges = [
  {
    icon: MdLocalShipping,
    title: "ارسال سراسری",
    subtitle: "به تمام نقاط ایران",
    color: { bg: "bg-[#eef4eb]", icon: "text-[#7a9e6f]" },
  },
  {
    icon: HiShieldCheck,
    title: "ضمانت اصالت",
    subtitle: "تضمین کیفیت محصولات",
    color: { bg: "bg-[#eef4eb]", icon: "text-[#5d8c5a]" },
  },
  {
    icon: MdSupportAgent,
    title: "پشتیبانی ۲۴/۷",
    subtitle: "همیشه در کنار شما",
    color: { bg: "bg-[#eef4eb]", icon: "text-[#7a9e6f]" },
  },
  {
    icon: BsAwardFill,
    title: "بیش از ۱۰ سال تجربه",
    subtitle: "در صنعت چوب ایران",
    color: { bg: "bg-[#eef4eb]", icon: "text-[#5d8c5a]" },
  },
];

const servicesList = [
  "سفارش سینی‌های چوبی با طرح دلخواه",
  "تولید سینی‌های چوبی با چوب مرغوب",
  "ارسال سریع به سراسر کشور",
  "گارانتی کیفیت و سلامت محصولات",
  "مشاوره رایگان برای انتخاب سینی مناسب",
];

const quickLinks = [
  { label: "صفحه اصلی", href: "/" },
  { label: "درباره ما", href: "/about" },
  { label: "تماس با ما", href: "/contact" },
  { label: "قوانین و مقررات", href: "/rules" },
  { label: "سوالات متداول", href: "/faq" },
];

const ServicesColumn = () => (
  <div className="flex flex-col gap-2.5 text-right">
    <h4 className="text-sm font-black text-[#2d2d2d] mb-1 border-b border-[#c8d8c0] pb-2.5">
      خدمات
    </h4>
    {servicesList.map((service, index) => (
      <span
        key={index}
        className="text-xs sm:text-sm text-gray-500 w-fit"
      >
        {service}
      </span>
    ))}
  </div>
);

const QuickAccessColumn = () => (
  <div className="flex flex-col gap-2.5 text-right">
    <h4 className="text-sm font-black text-[#2d2d2d] mb-1 border-b border-[#c8d8c0] pb-2.5">
      دسترسی سریع
    </h4>
    {quickLinks.map((link) => (
      <a
        key={link.label}
        href={link.href}
        className="text-xs sm:text-sm text-gray-500 hover:text-[#7a9e6f] hover:-translate-x-1
                   transition-all duration-200 w-fit"
      >
        {link.label}
      </a>
    ))}
  </div>
);

const CustomToast = ({ message, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      if (onClose) onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[9999] animate-fade-in-down">
      <div className="flex items-center gap-3 px-6 py-3.5 rounded-2xl shadow-2xl bg-white border-2 border-[#7a9e6f]">
        <span className="text-xl">✅</span>
        <span className="text-sm font-medium text-[#2d2d2d]">
          {message}
        </span>
        <button
          onClick={onClose}
          className="mr-2 text-gray-400 hover:text-[#7a9e6f] transition-colors duration-200 text-lg font-light"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

export default function Footer() {
  const [toast, setToast] = useState(null);

  const handleCopyTelegram = () => {
    const telegramId = "@Erisswood";

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(telegramId)
        .then(() => {
          setToast({ message: "آیدی تلگرام اریس گالری کپی شد ✅" });
        })
        .catch(() => {
          copyFallback(telegramId);
        });
    } else {
      copyFallback(telegramId);
    }
  };

  const copyFallback = (text) => {
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setToast({ message: "آیدی تلگرام اریس گالری کپی شد ✅" });
    } catch (err) {
      setToast({ message: "خطا در کپی کردن ❌" });
    }
  };

  const closeToast = () => setToast(null);

  const enamadLogoHTML = `<a referrerpolicy='origin' target='_blank' href='https://trustseal.enamad.ir/?id=8112483&Code=JTq5llmzSrb9jQYxCuJbXK6aYbz6xhoU'><img referrerpolicy='origin' src='https://trustseal.enamad.ir/logo.aspx?id=8112483&Code=JTq5llmzSrb9jQYxCuJbXK6aYbz6xhoU' alt='' style='cursor:pointer' code='JTq5llmzSrb9jQYxCuJbXK6aYbz6xhoU'></a>`;

  return (
    <footer className="w-full bg-[#f6f1e8] font-vazir" dir="rtl">
      {toast && (
        <CustomToast
          message={toast.message}
          onClose={closeToast}
        />
      )}

      <div className="w-full px-4 pt-8 flex justify-center">
        <div
          className="w-full max-w-4xl bg-white rounded-[22px] shadow-sm
                     border border-[#c8d8c0] mx-auto overflow-hidden"
        >
          <div className="grid grid-cols-2 md:grid-cols-4">
            {trustBadges.map((badge, i) => (
              <TrustBadge
                key={badge.title}
                {...badge}
                isLast={i === trustBadges.length - 1}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 mb-8 items-start">
          <div className="md:col-span-4 space-y-4 text-right">
            <div>
              <h2 className="text-xl font-black text-[#2d2d2d] mb-2.5">
                اریس گالری
              </h2>
              <p className="text-xs sm:text-sm text-gray-500 leading-7 text-justify">
                بیش از یک دهه تجربه در تأمین و عرضه انواع سینی‌های چوبی با
                بالاترین استانداردهای کیفی برای مشتریان سراسر ایران.
              </p>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <a
                href="https://www.instagram.com/eriss_wood?stkn=Ymt6ODE0MzY1NWE2"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center
                           border-2 border-[#7a9e6f] text-[#7a9e6f]
                           hover:bg-[#7a9e6f] hover:text-white hover:scale-105 active:scale-95
                           transition-all duration-300 shadow-sm"
                aria-label="اینستاگرام"
              >
                <FaInstagram className="text-base sm:text-lg" />
              </a>

              <button
                onClick={handleCopyTelegram}
                aria-label="تلگرام"
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center
                           border-2 border-[#7a9e6f] text-[#7a9e6f]
                           hover:bg-[#7a9e6f] hover:text-white hover:scale-105 active:scale-95
                           transition-all duration-300 shadow-sm cursor-pointer"
              >
                <FaTelegram className="text-base sm:text-lg" />
              </button>

              <a
                href="https://wa.me/989903607525"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center
                           border-2 border-[#7a9e6f] text-[#7a9e6f]
                           hover:bg-[#7a9e6f] hover:text-white hover:scale-105 active:scale-95
                           transition-all duration-300 shadow-sm"
                aria-label="واتساپ"
              >
                <FaWhatsapp className="text-base sm:text-lg" />
              </a>

              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center
                           border-2 border-[#7a9e6f] text-[#7a9e6f]
                           hover:bg-[#7a9e6f] hover:text-white hover:scale-105 active:scale-95
                           transition-all duration-300 shadow-sm"
                aria-label="لینکدین"
              >
                <FaLinkedin className="text-base sm:text-lg" />
              </a>
            </div>
          </div>

          <div className="md:col-span-8 grid grid-cols-2 gap-6 sm:gap-8">
            <ServicesColumn />
            <QuickAccessColumn />
          </div>
        </div>

        <div
          className="flex flex-col md:flex-row-reverse items-center justify-between
                     gap-6 border-t border-[#c8d8c0] pt-6"
        >
          <div className="flex items-center gap-3">
            <div
              className="flex items-center justify-center"
              dangerouslySetInnerHTML={{ __html: enamadLogoHTML }}
            />
          </div>

          <div className="text-center md:text-left text-xs sm:text-sm text-gray-500 leading-7">
            <p>
              مشهد، بلوار طرحچی، طرحچی ۳۲، خیابان صنعت، مجموعه تولیدی اریس گالری &nbsp;|&nbsp; تلفن:{" "}
              <a
                href="tel:09903607525"
                className="text-[#7a9e6f] font-bold hover:underline"
              >
                ۰۹۹۰۳۶۰۷۵۲۵
              </a>
            </p>
            <p>
              ایمیل:{" "}
              <a
                href="mailto:info@ariswood.ir"
                className="text-[#7a9e6f] font-bold hover:underline"
              >
                info@ariswood.ir
              </a>
            </p>
          </div>
        </div>

        <p className="text-center text-[11px] text-gray-400 mt-6">
          © ۱۴۰۵ اریس گالری — تمامی حقوق محفوظ است.
        </p>
      </div>

      <style jsx>{`
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translate(-50%, -30px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
        .animate-fade-in-down {
          animation: fadeInDown 0.5s ease-out forwards;
        }
      `}</style>
    </footer>
  );
}